import Result from 'antd/lib/result'
import React, { lazy, useEffect } from 'react'
import {
  createRoutesFromElements,
  createBrowserRouter,
  Route,
  Navigate,
  RouterProvider
} from 'react-router-dom'
import Discounts from 'components/Discounts'
import NotFound from 'components/Misc/NotFound'
import HelpCentre from 'components/Home/HelpCentre'
import Composer from 'components/Composer'
import Stream from 'components/Stream'
import OrdersPending from 'components/Orders/OrdersPending'
import LocationForm from 'components/Locations/LocationForm'
import OrdersCalendar from 'components/Orders/OrdersCalendar'
import PricingTable from 'components/Locations/PreOrderSettings/Delivery/PricingTable'
import { signOut } from '@slerp/accounts'
import { default as LocationSettings } from 'components/Locations/Settings'
import StripeRedirectRoute from 'components/Layout/StripeRedirectRoute'
import { useSession, isManager, isAdmin } from '@slerp/accounts'
import SignIn from 'components/SingleSignOn/SignIn'
import {
  AppCampaignsWrapper,
  ProductsAndModifiersWrapper,
  LocationsWrapper,
  IntegrationsWrapper,
  LocationDashboardWrapper,
  LoyaltyWrapper,
  ManageIntegrationsWrapper,
  LayoutWrapper,
  HomeWrapper,
  OrdersWrapper,
  SettingsWrapper,
  CustomersWrapper,
  DesignerReworkWrapper
} from 'router/Wrappers'

const SelectCreateDiscount = lazy(
  () => import('components/Discounts/SelectCreateDiscount')
)
const AvailabilityRules = lazy(
  () => import('components/Locations/AvailabilityRules')
)
const CategoryCutOffForm = lazy(
  () =>
    import(
      'components/Locations/AvailabilityRules/CategoryCutOffs/CategoryCutOffForm'
    )
)
const SelectCreateRules = lazy(
  () =>
    import(
      'components/Locations/AvailabilityRules/CategoryCutOffs/SelectCreateRules'
    )
)
const SpecialAvailabilityForm = lazy(
  () =>
    import(
      'components/Locations/AvailabilityRules/SpecialAvailability/SpecialAvailabilityForm'
    )
)

const DiscountForm = lazy(
  () => import('components/Discounts/DiscountForm/DiscountForm')
)

const Pending = () => (
  <Result
    status='info'
    title='This page is coming soon'
    subTitle='Sorry, but we are still working on this page. Please come back later.'
  />
)

const Router = () => {
  const { user } = useSession()

  const isAdminUser = isAdmin(user)
  const isManagerUser = isManager(user)

  const canManageRoutes = (
    <>
      <Route
        path='/location/:slug/inventory/same-day/edit/:tab'
        element={<LocationDashboardWrapper />}
      />
      <Route
        path='/location/:slug/inventory/pre-orders/edit/:tab'
        element={<LocationDashboardWrapper />}
      />
      <Route
        path='/location/:slug/availability-rules/create'
        element={<SelectCreateRules />}
      />
      <Route
        path='/location/:slug/availability-rules/category-cut-off/create'
        element={<CategoryCutOffForm />}
      />
      <Route
        path='/location/:slug/availability-rules/special-availability/create'
        element={<SpecialAvailabilityForm />}
      />
    </>
  )

  const nonAdminRoutes = (
    <Route
      path='/locations'
      element={
        <Navigate
          to={
            user?.stores?.[0]?.store?.slug
              ? `/location/${user?.stores?.[0]?.store?.slug}`
              : '/locations'
          }
          replace
        />
      }
    />
  )

  const adminRoutes = (
    <>
      <Route path='/integrations' element={<IntegrationsWrapper />} />
      <Route
        path='/integrations/:slug'
        element={<ManageIntegrationsWrapper />}
      />
      <Route
        path='/integrations/:slug/store/:storeId'
        element={<ManageIntegrationsWrapper />}
      />
      <Route path='/pending' element={<OrdersPending />} />
      <Route path='/calendar' element={<OrdersCalendar />} />
      <Route path='/orders' element={<OrdersWrapper />} />
      <Route
        path='/orders/generate-report/:reportTab'
        element={<OrdersWrapper />}
      />
      <Route path='/orders/:transactionId' element={<OrdersWrapper />} />
      <Route
        path='/designer'
        element={<Navigate to='/designer/web/landing' />}
      />
      <Route
        path='/designer/:tab/:subtab'
        element={<DesignerReworkWrapper />}
      />
      <Route path='/designer/:tab' element={<DesignerReworkWrapper />} />
      <Route path='/products' element={<ProductsAndModifiersWrapper />} />
      <Route
        path='/products/categories'
        element={<ProductsAndModifiersWrapper />}
      />
      <Route
        path='/products/categories/new'
        element={<ProductsAndModifiersWrapper />}
      />
      <Route path='/products/new' element={<ProductsAndModifiersWrapper />} />
      <Route
        path='/products/reorder'
        element={<ProductsAndModifiersWrapper />}
      />
      <Route path='/modifiers' element={<ProductsAndModifiersWrapper />} />
      <Route
        path='/modifiers/modifier-groups'
        element={<ProductsAndModifiersWrapper />}
      />
      <Route
        path='/modifiers/modifier-groups/new'
        element={<ProductsAndModifiersWrapper />}
      />
      <Route path='/modifiers/new' element={<ProductsAndModifiersWrapper />} />

      <Route path='/discounts/create' element={<SelectCreateDiscount />} />
      <Route path='/discounts/product/create' element={<DiscountForm />} />
      <Route
        path='/discounts/delivery-charge/create'
        element={<DiscountForm />}
      />
      <Route path='/discounts/basket/create' element={<DiscountForm />} />
      <Route path='/discounts/basket/:discount_id' element={<DiscountForm />} />
      <Route
        path='/discounts/product/:discount_id'
        element={<DiscountForm />}
      />
      <Route
        path='/discounts/delivery-charge/:discount_id'
        element={<DiscountForm />}
      />
      <Route path='/discounts/code/edit/:discount_id' element={<Discounts />} />
      <Route path='/discounts/manual' element={<Discounts />} />
      <Route path='/discounts/automatic' element={<Discounts />} />
      <Route path='/discounts/rewards' element={<Discounts />} />
      <Route path='/discounts' element={<Discounts />} />
      <Route path='/customers/customer-list' element={<CustomersWrapper />} />
      <Route path='/customers/guest-list' element={<CustomersWrapper />} />
      <Route path='/customers' element={<CustomersWrapper />} />
      <Route
        path='/loyalty/overview/bulk-actions'
        element={<LoyaltyWrapper />}
      />
      <Route path='/loyalty/overview' element={<LoyaltyWrapper />} />
      <Route path='/loyalty/cards/new' element={<LoyaltyWrapper />} />
      <Route path='/loyalty/cards' element={<LoyaltyWrapper />} />
      <Route path='/loyalty/bonuses/new' element={<LoyaltyWrapper />} />
      <Route path='/loyalty/bonuses' element={<LoyaltyWrapper />} />
      <Route path='/loyalty/settings' element={<LoyaltyWrapper />} />
      <Route path='/loyalty' element={<LoyaltyWrapper />} />
      <Route path='/locations' element={<LocationsWrapper />} />
      <Route path='/locations/new' element={<LocationForm />} />

      <Route
        path='/location/:slug/slerp-optimize-delivery-pricing/:id'
        element={<PricingTable />}
      />
      <Route path='/settings/*' element={<SettingsWrapper />} />
      <Route path='/app-campaigns/history' element={<AppCampaignsWrapper />} />
      <Route
        path='/app-campaigns/scheduled'
        element={<AppCampaignsWrapper />}
      />
      <Route path='/app-campaigns/new' element={<AppCampaignsWrapper />} />
      <Route path='/app-campaigns' element={<AppCampaignsWrapper />} />
      <Route path='/account' element={<Pending />} />
      <Route path='/composer' element={<Composer />} />
    </>
  )

  const router = React.useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={<LayoutWrapper />}>
            <Route path='/sso' element={<SignIn />} />

            {!isAdminUser && nonAdminRoutes}
            {(isAdminUser || isManagerUser) && canManageRoutes}
            {isAdminUser && adminRoutes}

            <Route path='/' element={<Navigate to='/analytics' replace />} />
            <Route path='/analytics' element={<HomeWrapper />} />
            <Route path='/heat-map' element={<HomeWrapper />} />
            <Route
              path='/location/:slug'
              element={<LocationDashboardWrapper />}
            />
            <Route
              path='/location/:slug/orders'
              element={<LocationDashboardWrapper />}
            />
            <Route
              path='/location/:slug/inventory/same-day'
              element={<LocationDashboardWrapper />}
            />
            <Route
              path='/location/:slug/inventory/pre-orders'
              element={<LocationDashboardWrapper />}
            />
            <Route
              path='/location/:slug/settings/*'
              element={<LocationSettings />}
            />
            <Route
              path='/location/:slug/availability-rules'
              element={<AvailabilityRules />}
            />
            <Route
              path='/location/:slug/availability-rules/category-cut-off/rule/:rule_id'
              element={<CategoryCutOffForm />}
            />
            <Route
              path='/location/:slug/availability-rules/special-availability/rule/:rule_id'
              element={<SpecialAvailabilityForm />}
            />
            <Route
              path='/locations/stripe/connect'
              element={<StripeRedirectRoute path='/locations/stripe/connect' />}
            />
            <Route path='/help-centre' element={<HelpCentre />} />
            <Route path='/slerp-menu-manager' element={<Stream />} />
            <Route path='/sign-out' element={<SignOut />} />

            <Route path='*' element={<NotFound />} />
          </Route>
        )
      ),
    [isAdminUser, isManagerUser, user?.stores?.[0]?.store?.slug]
  )

  return <RouterProvider router={router} />
}

const SignOut = () => {
  useEffect(() => {
    signOut()
  }, [])
  return <></>
}

export default Router
